import { Location, isPlatformBrowser } from '@angular/common'
import {
  Component,
  DestroyRef,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core'
import { Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { startOfDay } from 'date-fns'

import { LocalizedDatePipe } from '../../../shared/pipes/localized-date.pipe'

/** Time the confirmation stays on screen before the payment page takes over. */
const REDIRECT_DELAY_MS = 5000

@Component({
  selector: 'dhp-products-payment',
  templateUrl: './products-payment.component.html',
  styleUrl: './products-payment.component.scss',
  imports: [TranslatePipe, LocalizedDatePipe],
})
export class ProductsPaymentComponent {
  private readonly location = inject(Location)
  private readonly router = inject(Router)
  private readonly platformId = inject(PLATFORM_ID)
  private readonly destroyRef = inject(DestroyRef)

  paymentLink = signal<string | undefined>(undefined)
  startDate = signal<Date | undefined>(undefined)

  /**
   * Checkout always passes a start date along, also when the subscription
   * starts today. Only a start date in the future is worth mentioning.
   */
  readonly futureStartDate = computed(() => {
    const date = this.startDate()

    if (!date || Number.isNaN(date.getTime())) {
      return undefined
    }

    return startOfDay(date).getTime() > startOfDay(new Date()).getTime()
      ? date
      : undefined
  })

  constructor() {
    const state =
      (this.location.getState() as
        | {
            startDate?: Date | string
            paymentUrl?: string
          }
        | undefined) ?? {}

    const { startDate, paymentUrl } = state

    this.startDate.set(startDate ? new Date(startDate) : undefined)
    this.paymentLink.set(paymentUrl)

    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    if (!paymentUrl) {
      void this.router.navigate(['/products'])

      return
    }

    const redirect = setTimeout(() => {
      window.location.href = paymentUrl
    }, REDIRECT_DELAY_MS)

    this.destroyRef.onDestroy(() => {
      clearTimeout(redirect)
    })
  }
}
